import React from 'react';
import { motion } from 'framer-motion';
import { Card, CardContent } from '@/components/ui/card';
import { AlertTriangle, Shield, Zap, X, Check } from 'lucide-react';

const PainPointsSection = () => {
  const painPoints = [
    {
      icon: AlertTriangle,
      title: "Parents miss big moments?",
      description: "Important recitals, competitions, and breakthroughs go unseen by busy parents.",
      color: "text-red-600 bg-red-100"
    },
    {
      icon: Shield,
      title: "YouTube or Dropbox isn't secure?",
      description: "Your videos mixed with ads, algorithms, and privacy concerns that worry parents.",
      color: "text-orange-600 bg-orange-100"
    },
    {
      icon: Zap,
      title: "Tired of tech headaches?",
      description: "Lost files, complicated sharing, and tools not built for activity programs.",
      color: "text-yellow-600 bg-yellow-100"
    }
  ];

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2,
        delayChildren: 0.1
      }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.6 }
    }
  };

  return (
    <section className="py-20 bg-gray-50">
      <div className="container mx-auto px-6 lg:px-8">
        {/* Pain Points */}
        <motion.div
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-100px" }}
          className="max-w-6xl mx-auto"
        >
          <motion.div variants={itemVariants} className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Sound Familiar?
            </h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              Every activity center faces these challenges when sharing videos with families
            </p>
          </motion.div>

          <div className="grid md:grid-cols-3 gap-8 mb-16">
            {painPoints.map((point, index) => (
              <motion.div key={index} variants={itemVariants}>
                <Card className="h-full border-2 border-gray-200 hover:border-red-200 transition-all duration-300 hover:shadow-lg">
                  <CardContent className="p-8 text-center">
                    <div className={`${point.color} rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-6`}>
                      <point.icon className="h-8 w-8" />
                    </div>
                    <h3 className="text-xl font-bold text-gray-900 mb-4">
                      {point.title}
                    </h3>
                    <p className="text-gray-600 leading-relaxed">
                      {point.description}
                    </p>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>
        </motion.div>

        {/* Solution Transition */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          className="max-w-4xl mx-auto"
        >
          {/* Problem vs Solution */}
          <div className="bg-white rounded-2xl shadow-xl p-8 md:p-12">
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
                There's a Better Way
              </h2>
              <p className="text-xl text-gray-600">
                HiveShare: Built specifically for activity centers, not social media
              </p>
            </div>

            <div className="grid md:grid-cols-2 gap-12 items-center">
              {/* Before */}
              <div>
                <h3 className="text-2xl font-bold text-red-600 mb-6 flex items-center">
                  <X className="h-6 w-6 mr-3" />
                  Before HiveShare
                </h3>
                <ul className="space-y-4">
                  <li className="flex items-start">
                    <X className="h-5 w-5 text-red-500 mr-3 mt-0.5 flex-shrink-0" />
                    <span className="text-gray-700">Videos lost in email chains and USB drives</span>
                  </li>
                  <li className="flex items-start">
                    <X className="h-5 w-5 text-red-500 mr-3 mt-0.5 flex-shrink-0" />
                    <span className="text-gray-700">Parents worry about privacy and ads</span>
                  </li>
                  <li className="flex items-start">
                    <X className="h-5 w-5 text-red-500 mr-3 mt-0.5 flex-shrink-0" />
                    <span className="text-gray-700">Hours spent manually organizing and sharing</span>
                  </li>
                  <li className="flex items-start">
                    <X className="h-5 w-5 text-red-500 mr-3 mt-0.5 flex-shrink-0" />
                    <span className="text-gray-700">No control over who sees what</span>
                  </li>
                </ul>
              </div>

              {/* After */}
              <div>
                <h3 className="text-2xl font-bold text-green-600 mb-6 flex items-center">
                  <Check className="h-6 w-6 mr-3" />
                  With HiveShare
                </h3>
                <ul className="space-y-4">
                  <li className="flex items-start">
                    <Check className="h-5 w-5 text-green-500 mr-3 mt-0.5 flex-shrink-0" />
                    <span className="text-gray-700">Upload from phone, tag by class/family</span>
                  </li>
                  <li className="flex items-start">
                    <Check className="h-5 w-5 text-green-500 mr-3 mt-0.5 flex-shrink-0" />
                    <span className="text-gray-700">Secure, invite-only access - no ads ever</span>
                  </li>
                  <li className="flex items-start">
                    <Check className="h-5 w-5 text-green-500 mr-3 mt-0.5 flex-shrink-0" />
                    <span className="text-gray-700">Parents get instant access via iOS app</span>
                  </li>
                  <li className="flex items-start">
                    <Check className="h-5 w-5 text-green-500 mr-3 mt-0.5 flex-shrink-0" />
                    <span className="text-gray-700">Complete family privacy controls</span>
                  </li>
                </ul>
              </div>
            </div>

            {/* Key Benefit Callout */}
            <div className="mt-12 bg-gradient-to-r from-blue-50 to-blue-100 rounded-xl p-8 text-center">
              <p className="text-2xl font-bold text-blue-900 mb-2">
                "Finally, a platform designed for kids programs, not social media."
              </p>
              <p className="text-blue-700">
                - What our early users are saying
              </p>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default PainPointsSection;